import { plainAddressError } from './errorDetail';
import { API_BASE_URL } from './config';

// Lightweight API fetch wrapper that attaches the JWT from localStorage
// and calls an onUnauthorized callback (e.g. AuthContext.logout) when a 401 is received.
const TOKEN_STORAGE_KEY = 'token';
let refreshPromise = null;

// Exported so a caller that needs to *show* the trace id — an applicant reading a
// reference code off a failed submission — can mint the same id the server will log.
export function createRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function resolveClientApp() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (
    path.startsWith('/public') ||
    path === '/my-applications' ||
    path.startsWith('/my-applications/') ||
    path.startsWith('/register') ||
    path.startsWith('/apply') ||
    path.startsWith('/payment')
  ) {
    return 'public-web';
  }
  return 'web';
}

export function getStoredAccessToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

export function setStoredAccessToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (e) {
    // Ignore storage failures; the in-memory AuthContext still owns current state.
  }
  window.dispatchEvent(new CustomEvent('auth:token-changed', { detail: token }));
}

export function clearStoredAccessToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    // Ignore storage failures.
  }
  window.dispatchEvent(new CustomEvent('auth:token-changed', { detail: null }));
}

function isAuthEndpoint(url) {
  return url.endsWith('/login') || url.endsWith('/auth/refresh') || url.endsWith('/logout');
}

// Why a refresh attempt ended. The distinction matters: only the server telling
// us the refresh cookie is no good proves the session is over. A dropped
// connection or a 502 says nothing about the session, and treating it as proof
// signs people out mid-task over a blip — which is exactly the wrong behaviour
// in an app whose users work offline in the field.
export const REFRESH_OK = 'ok';
export const REFRESH_REJECTED = 'rejected';
export const REFRESH_UNAVAILABLE = 'unavailable';

async function performRefresh() {
  const headers = new Headers();
  headers.set('X-Request-ID', createRequestId());
  headers.set('X-Client-App', resolveClientApp());

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });
  } catch (e) {
    // Offline, DNS failure, CORS — we never reached the server. Keep the token.
    return { token: null, outcome: REFRESH_UNAVAILABLE };
  }

  if (response.status === 401 || response.status === 403) {
    // The server looked at the cookie and rejected it. The session is over.
    clearStoredAccessToken();
    return { token: null, outcome: REFRESH_REJECTED };
  }

  if (!response.ok) {
    // 5xx, 429, anything else — a server problem, not a verdict on the session.
    return { token: null, outcome: REFRESH_UNAVAILABLE };
  }

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  if (!data?.access_token) {
    // A 200 with no token is a broken response, not a rejection.
    return { token: null, outcome: REFRESH_UNAVAILABLE };
  }

  setStoredAccessToken(data.access_token);
  return { token: data.access_token, outcome: REFRESH_OK };
}

const REFRESH_LOCK_NAME = 'civicode-auth-refresh';

/**
 * Run the refresh under a lock shared by every tab on this origin.
 *
 * `refreshPromise` only serialises callers inside one tab. The refresh cookie is
 * shared across all of them, so two tabs refreshing at once can both send the
 * same cookie, and whichever loses the rotation is replaying a dead token — which
 * the server reads as theft. The Web Locks API is the only cross-tab primitive
 * that releases correctly when a tab is closed mid-request; where it is missing
 * we fall back to per-tab serialisation and lean on the server's grace window.
 */
async function refreshUnderCrossTabLock(tokenBeforeWait) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks || typeof locks.request !== 'function') {
    return performRefresh();
  }

  return locks.request(REFRESH_LOCK_NAME, async () => {
    // Another tab may have rotated while we queued for the lock. Its new token is
    // already in storage, so take it rather than burning a second rotation.
    const currentToken = getStoredAccessToken();
    if (currentToken && currentToken !== tokenBeforeWait) {
      return { token: currentToken, outcome: REFRESH_OK };
    }
    return performRefresh();
  });
}

/**
 * Refresh the session, collapsing concurrent callers onto one request.
 *
 * The single-flight guard is a correctness requirement, not an optimisation:
 * the backend rotates the refresh token on every use and treats replay of a
 * rotated one as theft, revoking every session the user has
 * (routes/users.py, refresh_auth_session).
 */
export async function refreshSession() {
  if (!refreshPromise) {
    const tokenBeforeWait = getStoredAccessToken();
    refreshPromise = refreshUnderCrossTabLock(tokenBeforeWait).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function refreshAccessToken() {
  const { token } = await refreshSession();
  return token;
}

export function getUnauthorizedRedirectPath() {
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  const loginPath = resolveClientApp() === 'public-web' ? '/public-login' : '/login';
  return `${loginPath}?from=${next}`;
}

async function handleUnauthorized({ onUnauthorized }) {
  if (typeof onUnauthorized === 'function') {
    onUnauthorized();
    return;
  }

  clearStoredAccessToken();
  window.location.replace(getUnauthorizedRedirectPath());
}

// Keep Response status, headers, text and body consumption semantics unchanged.
function withPlainAddressErrors(response) {
  if (response.ok || typeof response.json !== 'function') return response;
  const readJson = response.json.bind(response);
  response.json = async () => plainAddressError(await readJson());
  return response;
}

export async function apiFetch(input, init = {}, {
  onUnauthorized,
  skipAuth = false,
  skipRefresh = false,
  background = false,
} = {}) {
  const url = typeof input === 'string' && (input.startsWith('http://') || input.startsWith('https://'))
    ? input
    : `${API_BASE_URL}${input}`;

  const token = getStoredAccessToken();

  const headers = new Headers(init.headers || {});
  if (!skipAuth && token) {
    const authorization = headers.get('Authorization');
    if (!authorization || authorization.toLowerCase().startsWith('bearer ')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  if (!headers.has('X-Request-ID')) {
    headers.set('X-Request-ID', createRequestId());
  }
  if (!headers.has('X-Client-App')) {
    headers.set('X-Client-App', resolveClientApp());
  }

  let body = init.body;
  if (
    body &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof URLSearchParams)
  ) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    body = JSON.stringify(body);
  }

  const opts = { ...init, credentials: init.credentials || 'include', headers, body };

  let res;
  try {
    res = await fetch(url, opts);
  } catch (e) {
    // network error or aborted - propagate
    throw e;
  }

  if (res.status !== 401) return withPlainAddressErrors(res);

  if (!skipAuth && !skipRefresh && !isAuthEndpoint(url)) {
    const { token: refreshedToken, outcome } = await refreshSession().catch(() => ({
      token: null,
      outcome: REFRESH_UNAVAILABLE,
    }));
    if (refreshedToken) {
      const retryHeaders = new Headers(headers);
      retryHeaders.set('Authorization', `Bearer ${refreshedToken}`);
      return withPlainAddressErrors(await fetch(url, { ...opts, headers: retryHeaders }));
    }
    if (outcome === REFRESH_UNAVAILABLE) {
      // We could not reach the refresh endpoint, so we do not know the session
      // is over. Hand the 401 back and let the caller retry; ending the session
      // here would sign the user out over a dropped connection.
      return withPlainAddressErrors(res);
    }
  }

  // A poll running in the background must never be able to end a session the
  // user is actively working in. Surface the 401 and let it fail quietly.
  if (background) return withPlainAddressErrors(res);

  await handleUnauthorized({ onUnauthorized }).catch(() => {});

  return withPlainAddressErrors(res);
}

/**
 * Multipart upload with progress. `fetch` cannot report how far an upload has
 * got, so photo capture — where the user is standing outside holding a phone,
 * watching nothing happen — goes over XHR instead.
 *
 * Header and 401 handling mirror apiFetch exactly; the resolved value is
 * Response-shaped so call sites keep their `res.ok` / `await res.json()` code.
 */
export function apiUpload(input, formData, {
  onProgress,
  signal,
  method = 'POST',
  onUnauthorized,
  skipRefresh = false,
  background = false,
  requestId,
} = {}) {
  const url = typeof input === 'string' && (input.startsWith('http://') || input.startsWith('https://'))
    ? input
    : `${API_BASE_URL}${input}`;

  const token = getStoredAccessToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);

    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('X-Request-ID', requestId || createRequestId());
    xhr.setRequestHeader('X-Client-App', resolveClientApp());

    if (typeof onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };
    }

    const abort = () => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', abort, { once: true });
    }

    const cleanup = () => signal?.removeEventListener('abort', abort);

    xhr.onload = async () => {
      cleanup();
      const text = xhr.responseText;

      let refreshOutcome = null;
      if (xhr.status === 401 && !skipRefresh) {
        const { token: refreshedToken, outcome } = await refreshSession().catch(() => ({
          token: null,
          outcome: REFRESH_UNAVAILABLE,
        }));
        refreshOutcome = outcome;
        if (refreshedToken) {
          resolve(apiUpload(input, formData, {
            onProgress,
            signal,
            method,
            onUnauthorized,
            background,
            // Keep one trace id across the refresh rather than silently forking it.
            requestId,
            skipRefresh: true,
          }));
          return;
        }
      }

      // Mirrors apiFetch: only end the session when the server actually
      // rejected the refresh, and never on behalf of a background request.
      if (
        xhr.status === 401 &&
        refreshOutcome !== REFRESH_UNAVAILABLE &&
        !background
      ) {
        await handleUnauthorized({ onUnauthorized }).catch(() => {});
      }

      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        text: async () => text,
        json: async () => {
          const payload = text ? JSON.parse(text) : null;
          return xhr.status >= 200 && xhr.status < 300 ? payload : plainAddressError(payload);
        },
      });
    };

    xhr.onerror = () => {
      cleanup();
      reject(new TypeError('Network request failed'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    xhr.send(formData);
  });
}

export default apiFetch;
